/* istanbul ignore file */

import type { ComponentProps } from 'react';
import React, { memo } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  IllusEmpty_1,
  IllusLocation,
  ShadowDivider,
  theme,
  useResponsive,
} from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import type { LocationSearchMachineContext } from '../../../../machine';
import { LocationResults } from '../../../LocationResults';
import { LocationResultsListInfoMessage } from '../../../LocationResultsListInfoMessage';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchLocationsResults = memo(
  (props: LocationSearchLocationsResultsProps) => {
    const {
      locationSearchType,
      locations,
      focusedLocationId,
      isLoading,
      interactedLocationRestaurantSlug,
      withSafeAreaInsets,
      onLocationCardPress,
      onLocationCardFocus,
      onLocationResultsExpandCollapse,
    } = props;

    const { t } = useLocalizationContext();
    const { minWidth, match } = useResponsive();

    const { pickup: pickupLocations = [], outpost: outpostLocations = [] } =
      locations ?? {};

    const activeLocations =
      locationSearchType === 'outpost' ? outpostLocations : pickupLocations;
    const otherLocations =
      locationSearchType === 'outpost' ? pickupLocations : outpostLocations;

    const hasResolvedLocations = locations !== undefined;
    const noLocationsFound =
      hasResolvedLocations &&
      activeLocations.length + otherLocations.length === 0;

    const shouldRenderIntroMessage = !isLoading && !hasResolvedLocations;
    const shouldRenderNoLocationsFoundMessage = !isLoading && noLocationsFound;

    // ─────────────────────────────────────────────────────────────────────

    const containerStyles = match([styles.containerXS, styles.containerSM]);
    const containerPointerEvents = minWidth.isSM ? 'auto' : 'box-none';
    const shouldRenderResultsTopShadow = minWidth.isSM;

    // ─────────────────────────────────────────────────────────────────────

    if (shouldRenderIntroMessage)
      return (
        <LocationResultsListInfoMessage
          illustration={IllusLocation}
          illustrationAriaLabel={t('location.location-illustration.alt')}
          text={t('location.enable-location-services')}
        />
      );

    if (shouldRenderNoLocationsFoundMessage)
      return (
        <LocationResultsListInfoMessage
          illustration={IllusEmpty_1}
          illustrationAriaLabel={t('location.empty-results-illustration.alt')}
          title={t('location.no-results.banner')}
          text={t('location.try-another-search')}
        />
      );

    return (
      <View style={containerStyles} pointerEvents={containerPointerEvents}>
        {shouldRenderResultsTopShadow ? (
          <View style={styles.resultsTopShadowContainer}>
            <ShadowDivider
              height={theme.spacing['4']}
              color={theme.colors.OATMEAL}
            />
          </View>
        ) : null}

        <LocationResults
          locationSearchType={locationSearchType}
          locations={activeLocations}
          otherLocations={otherLocations}
          activePinLocationId={focusedLocationId}
          interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
          isLoading={isLoading}
          withSafeAreaInsets={withSafeAreaInsets}
          shouldHideDetailsModal={false}
          onCardPress={onLocationCardPress}
          onCardFocus={onLocationCardFocus}
          onLocationResultsExpandCollapse={onLocationResultsExpandCollapse}
        />
      </View>
    );
  },
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    ...StyleSheet.absoluteFillObject,
    zIndex: theme.zIndex.fixed,
  },
  containerSM: {
    flex: 1,
  },
  resultsTopShadowContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    zIndex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchLocationsResultsProps = Readonly<{
  locationSearchType: LocationSearchMachineContext['locationSearchType'];
  locations: LocationSearchMachineContext['locations'];
  isLoading: boolean;
  interactedLocationRestaurantSlug: string | undefined;
  focusedLocationId?: string;
  withSafeAreaInsets?: boolean;
  onLocationCardPress: ComponentProps<typeof LocationResults>['onCardPress'];
  onLocationCardFocus: ComponentProps<typeof LocationResults>['onCardFocus'];
  onLocationResultsExpandCollapse: ComponentProps<
    typeof LocationResults
  >['onLocationResultsExpandCollapse'];
}>;
